import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, useRouteUnmountEffect} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {efTransportationTypes, efServiceAssignmentTypes, apmContractPartnerAssignmentChannelTypes} from '@ace-de/eua-entity-types';
import {useStyles, ContentBlock, ContentItem} from '@ace-de/ui-components';
import * as userProfileSelectors from '../user-profiles/userProfileSelectors';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import * as serviceAssignmentSelectors from '../service-assignments/serviceAssignmentSelectors';
import * as sadehActionTypes from './sadehActionTypes';
import * as serviceAssignmentActionTypes from '../service-assignments/serviceAssignmentActionTypes';
import DocumentUploadPanel from '../service-cases/ui-elements/DocumentUploadPanel';
import CommissioningBasicDataPanel from '../service-assignments/ui-elements/CommissioningBasicDataPanel';
import CommissioningContactPersonPanel from '../service-assignments/ui-elements/CommissioningContactPersonPanel';
import CommissioningJourneyDataPanel from '../service-assignments/ui-elements/CommissioningJourneyDataPanel';
import CommissioningACEPartnerPanel from '../service-assignments/ui-elements/CommissioningACEPartnerPanel';
import config from '../config';
import VehicleInfoPanel from '../service-cases/ui-elements/VehicleInfoPanel';
import TrailerInfoPanel from '../service-assignments/ui-elements/TrailerInfoPanel';
import CostCoverageCommissioningPanel from './ui-elements/CostCoverageCommissioningPanel';
import CommissioningAdditionalVehicleInformationPanel from '../service-assignments/ui-elements/CommissioningAdditionalVehicleInformationPanel';
import CostCoverageDeathInfoPanel from './ui-elements/CostCoverageDeathInfoPanel';
import isACEPartnerFromDACH from '../service-assignments/isACEPartnerFromDACH';

const SADEHCostCoverageTab = props => {
    const {cx} = useStyles();
    const {translate} = useTranslate();
    const {serviceCase, serviceAssignment, submitSADEHCostCoverageForm, users} = props;
    const {submitSADEHCostCoverageServiceProviderForm, submitCreateSADEHPDF} = props;
    const {hasToResetAttachmentSelection, storeHasToResetAttachmentSelection} = props;
    const [selfPayer, setSelfPayer] = useState({
        hasSelfPayerOption: serviceAssignment.selfPayerOption,
        selfPayerNote: serviceAssignment.selfPayerNote,
    });
    const [assignmentText, setAssignmentText] = useState(serviceAssignment?.assignmentText || '');
    const [callbackPhoneNo, setCallbackPhoneNo] = useState(serviceAssignment.callbackPhoneNo || '');
    const [fallbackSystemId, setFallbackSystemId] = useState(serviceAssignment?.fallbackSystemId || '');
    const [isFallbackSystemIdEnabled, setIsFallbackSystemIdEnabled] = useState(
        !!serviceAssignment?.isFallbackSystemIdEnabled,
    );
    const [additionalInformationText, setAdditionalInformationText] = useState(
        serviceAssignment?.additionalInformationText || '',
    );
    const [attachmentsToSendWithAssignment, setAttachmentsToSendWithAssignment] = useState([]);

    useEffect(() => {
        if (!hasToResetAttachmentSelection) return;
        setAttachmentsToSendWithAssignment([]);
        storeHasToResetAttachmentSelection({hasToResetAttachmentSelection: false});
    }, [hasToResetAttachmentSelection, storeHasToResetAttachmentSelection, setAttachmentsToSendWithAssignment]);

    const onChangeHandler = (key, value) => {
        if (key === 'assignmentText') {
            setAssignmentText(value);
            return;
        }

        if (key === 'callbackPhoneNo') {
            setCallbackPhoneNo(value);
            return;
        }

        if (key === 'additionalInformationText' && value.length <= config.MAXIMUM_TEXT_AREA_CONTENT_LENGTH) {
            setAdditionalInformationText(value);
            return;
        }

        if (key === 'fallbackSystemId') {
            setFallbackSystemId(value);
            return;
        }

        if (key === 'isFallbackSystemIdEnabled') {
            setIsFallbackSystemIdEnabled(value);
            setFallbackSystemId('');
            return;
        }

        if (key === 'hasSelfPayerOption') {
            setSelfPayer({
                ...selfPayer,
                [key]: value,
                selfPayerNote: '',
            });
            return;
        }

        if (key === 'selfPayerNote' && value.length <= config.MAXIMUM_SELF_PAYER_NOTE_LENGTH) {
            setSelfPayer({
                ...selfPayer,
                [key]: value,
            });
        }
    };

    const handleOnSend = serviceProviderData => {
        const serviceAssignmentData = {
            selfPayerOption: selfPayer.hasSelfPayerOption,
            selfPayerNote: selfPayer.selfPayerNote,
            assignmentText,
            additionalInformationText,
            callbackPhoneNo,
            fallbackSystemId,
            isFallbackSystemIdEnabled,
            ...serviceProviderData,
        };

        submitSADEHCostCoverageServiceProviderForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            assignmentType: efServiceAssignmentTypes.DEATH,
            serviceAssignmentData,
            attachmentsToSendWithAssignment,
        });

        // on send, reset attachments state: see https://computerrock.atlassian.net/browse/ACEECS-5792
        setAttachmentsToSendWithAssignment([]);
    };

    useRouteUnmountEffect(({completeRouteUnmountSideEffect}) => {
        if (!selfPayer) {
            completeRouteUnmountSideEffect({
                caller: sadehActionTypes.SUBMIT_SADEH_COST_COVERAGE_FORM,
            });
            return;
        }

        submitSADEHCostCoverageForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData: {
                selfPayerOption: selfPayer.hasSelfPayerOption,
                selfPayerNote: selfPayer.selfPayerNote,
                assignmentText,
                additionalInformationText,
                callbackPhoneNo,
                fallbackSystemId,
                isFallbackSystemIdEnabled,
            },
            attachmentsToSendWithAssignment,
        });
    }, [
        selfPayer,
        serviceAssignment.lineNo,
        serviceAssignment.serviceCaseId,
        assignmentText,
        additionalInformationText,
        callbackPhoneNo,
        fallbackSystemId,
        isFallbackSystemIdEnabled,
        submitSADEHCostCoverageForm,
        attachmentsToSendWithAssignment,
    ]);

    const submitCreatePDF = serviceProviderData => {
        const serviceAssignmentData = {
            selfPayerOption: selfPayer.hasSelfPayerOption,
            selfPayerNote: selfPayer.selfPayerNote,
            assignmentText,
            additionalInformationText,
            callbackPhoneNo,
            fallbackSystemId,
            isFallbackSystemIdEnabled,
            ...serviceProviderData,
        };

        const isDACH = isACEPartnerFromDACH(serviceAssignment?.acePartner);

        submitCreateSADEHPDF({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentType: efServiceAssignmentTypes.DEATH,
            serviceAssignmentData,
            attachmentsToSendWithAssignment,
            prefix: serviceCase.prefix,
            serviceType: isDACH
                ? translate(`global.service_type.${serviceAssignment.assignmentType.toLowerCase()}`)
                : serviceAssignment.assignmentType.toLowerCase(),
        });
    };

    // if no case or assignment don't render
    if (!serviceCase || !serviceAssignment) return null;

    return (
        <ContentBlock
            className={cx([
                'global!ace-u-height--full',
                'global!ace-u-max-height--full',
                'global!ace-u-flex--align-stretch',
            ])}
        >
            <ContentItem className={cx('ace-c-content-item--span-9')}>
                <CommissioningBasicDataPanel
                    selfPayer={selfPayer}
                    agentUserProfile={users[serviceAssignment.createdBy]}
                    onChangeHandler={onChangeHandler}
                    callbackPhoneNo={callbackPhoneNo}
                    additionalInformationText={additionalInformationText}
                    fallbackSystemId={fallbackSystemId}
                    isFallbackSystemIdEnabled={isFallbackSystemIdEnabled}
                />
                <CostCoverageCommissioningPanel
                    assignmentText={assignmentText}
                    onChangeHandler={onChangeHandler}
                />
                <CommissioningContactPersonPanel
                    title={translate('sadeh_cost_coverage_tab.panel_title.contact_person_at_location')}
                    contactPerson={serviceAssignment.contactPersonOnSite}
                />
                <CommissioningContactPersonPanel
                    title={translate('sadeh_cost_coverage_tab.panel_title.affected')}
                    contactPerson={serviceAssignment.affectedPerson}
                    isAffectedPerson={true}
                />
                <CommissioningContactPersonPanel
                    title={translate('sadeh_cost_coverage_tab.panel_title.contact_person_at_home')}
                    contactPerson={serviceAssignment.contactPersonAtHome}
                />
                <CostCoverageDeathInfoPanel />
                <CommissioningJourneyDataPanel />
                {serviceAssignment.transportationType === efTransportationTypes.PERSONAL_VEHICLE ? (
                    <VehicleInfoPanel />
                ) : null}
                {serviceAssignment.transportationType === efTransportationTypes.PERSONAL_VEHICLE ? (
                    <CommissioningAdditionalVehicleInformationPanel />
                ) : null}
                {serviceCase.trailer ? (
                    <TrailerInfoPanel />
                ) : null}
            </ContentItem>
            <ContentItem className={cx('ace-c-content-item--span-3')}>
                <CommissioningACEPartnerPanel
                    assignmentChannels={[
                        apmContractPartnerAssignmentChannelTypes.EMAIL_PDF_XML,
                        apmContractPartnerAssignmentChannelTypes.FAX,
                    ]}
                    handleOnAssignmentSubmit={handleOnSend}
                    attachmentsToSendWithAssignment={attachmentsToSendWithAssignment}
                    submitCreatePDFForm={submitCreatePDF}
                />
                {!config.IS_FALLBACK_SYSTEM && (
                    <DocumentUploadPanel
                        attachmentsToSendWithAssignment={attachmentsToSendWithAssignment}
                        setAttachmentsToSendWithAssignment={setAttachmentsToSendWithAssignment}
                    />
                )}
            </ContentItem>
        </ContentBlock>
    );
};

SADEHCostCoverageTab.propTypes = {
    submitSADEHCostCoverageForm: PropTypes.func.isRequired,
    serviceCase: PropTypes.object,
    serviceAssignment: PropTypes.object,
    users: PropTypes.object,
    submitSADEHCostCoverageServiceProviderForm: PropTypes.func.isRequired,
    submitCreateSADEHPDF: PropTypes.func.isRequired,
    hasToResetAttachmentSelection: PropTypes.bool,
    storeHasToResetAttachmentSelection: PropTypes.func.isRequired,
};

SADEHCostCoverageTab.defaultProps = {
    serviceCase: null,
    serviceAssignment: null,
    users: {},
    hasToResetAttachmentSelection: false,
};

const mapStateToProps = (state, props) => {
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    return {
        serviceCase: serviceCaseSelector(state, props),
        serviceAssignment: serviceAssignmentSelector(state, props),
        users: userProfileSelectors.getUsers(state),
        hasToResetAttachmentSelection: state.serviceAssignments.hasToResetAttachmentSelection,
    };
};

const mapDispatchToProps = dispatch => ({
    submitSADEHCostCoverageForm: payload => dispatch({
        type: sadehActionTypes.SUBMIT_SADEH_COST_COVERAGE_FORM,
        payload,
    }),
    submitSADEHCostCoverageServiceProviderForm: payload => dispatch({
        type: serviceAssignmentActionTypes.SUBMIT_COMMISSIONING_ACE_PARTNER_FORM,
        payload,
    }),
    submitCreateSADEHPDF: payload => dispatch({
        type: serviceAssignmentActionTypes.CREATE_SERVICE_ASSIGNMENT_PDF,
        payload,
    }),
    storeHasToResetAttachmentSelection: payload => dispatch({
        type: serviceAssignmentActionTypes.STORE_HAS_TO_RESET_ASSIGNMENT_SELECTION,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SADEHCostCoverageTab));
